import { useState, useEffect, useCallback, RefObject } from "react";

export interface ScreenDetectorResult {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

const getScreenResult = (width: number): ScreenDetectorResult => ({
  isMobile: width <= 768,
  isTablet: width <= 1024 && width > 768,
  isDesktop: width > 1024,
});

const getInitialWidth = () =>
  typeof window !== "undefined" ? window.innerWidth : 1200;

export const useScreenDetector = (
  containerRef?: RefObject<HTMLElement | null>
): ScreenDetectorResult => {
  const [result, setResult] = useState<ScreenDetectorResult>(() =>
    getScreenResult(getInitialWidth())
  );

  const updateResult = useCallback((width: number) => {
    setResult((prev) => {
      const next = getScreenResult(width);

      if (
        prev.isMobile === next.isMobile &&
        prev.isTablet === next.isTablet &&
        prev.isDesktop === next.isDesktop
      ) {
        return prev;
      }

      return next;
    });
  }, []);

  useEffect(() => {
    const element = containerRef?.current;
    if (!element) return;

    updateResult(element.clientWidth);

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      
      if (entry) {
        updateResult(entry.contentRect.width);
      }
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [containerRef, updateResult]);

  return result;
};
